/**
 * Transfers between your own ships while they are together (M10 P5a8b; plan `docs/plans/2026-10-17-m10-p5a.md`
 * §3.5, Q1): cargo lots move between two of the player's ships, and locker modules through P5a5a's `moveModule`.
 *
 * - **Together:** the carried S and its carrier (`career.carriedBy`, either way), or both docked or moored at the same
 *   station (`ShipPlaces`, read by the caller from the fleet view: the Career doesn't know where fleet ships are).
 *   Refused otherwise (`serviceUnavailable`, `reason: 'notTogether'`).
 * - **Holds:** the flown ship's hold is `career.cargo`; another ship's is its hangar entry's `hold` (Q1; absent =
 *   empty, at the ship's rated capacity).
 * - **Class-weighted:** the target's hold after the move is counted with `holdUsedT` against `holdCapacityT` on the
 *   target's def (its `fit.holdMult` per cargo class; every shipped hull is 1×, so this is today's raw sum). Over
 *   capacity is refused `cargoFull` and nothing moves.
 * - **Processed tonnes travel with the wares** (P5a12d precondition): a ware's processed share moves in proportion
 *   to the tonnes moved, and each side is clamped to at most its wares, so a hold never keeps processed > wares.
 * - Contract, sealed and story components stay with the player's ship (D-402): moving one off the flown ship is
 *   refused.
 * - Free: the wallet is untouched. Pure: the input career is never changed. Event rate (a button), not per frame.
 *
 * `moor.json transfers.enabled` (false until W6) gates the panel (`transfersEnabled`), not these functions.
 */
import type { M2Data, ModuleId, WareId } from '../data/m2Schemas'
import { moorConfig, type MoorConfig } from '../docking/moorConfig'
import { moveModule, type ShipPlaces } from '../career/fitting'
import { holdCapacityT, holdUsedT, hullFitData, type HullFitData } from '../career/hullFit'
import { EPSILON, emptyWares } from '../career/ops'
import type { GoodId } from '../career/parts'
import { fail, formatTonnes, invalidAmount, ok, type CmdResult } from '../career/result'
import type { CargoState, Career, ShipState } from '../career/types'
import { OWN_SHIP_ID, hangarOf, ownedShip, updateOwnedShip } from './hangar'

export type { ShipPlaces } from '../career/fitting'

/** One cargo lot to move: tonnes of a ware, units of a good (the goods block), or one component by id. */
export type TransferLot =
  | { kind: 'ware'; ware: WareId; tonnes: number }
  | { kind: 'good'; good: GoodId; qty: number }
  | { kind: 'component'; id: string }

/** The transfer panel's gate: `moor.json transfers.enabled` (absent = off). */
export function transfersEnabled(cfg: Pick<MoorConfig, 'transfers'> = moorConfig()): boolean {
  return cfg.transfers?.enabled === true
}

/** The flown ship's fleet id (`OWN_SHIP_ID` for the lease ship). */
function flownIdOf(career: Career): string {
  return career.flownShip ?? OWN_SHIP_ID
}

/**
 * True when ships `a` and `b` (fleet ids) are two different ships of the player's that are together: the carried S
 * and its carrier, or both at one station (`places`, which gives null for a ship in space).
 */
export function shipsTogether(career: Career, a: string, b: string, places: ShipPlaces): boolean {
  if (a === b) return false
  const flown = flownIdOf(career)
  const l = career.carriedBy
  if (l !== undefined && ((a === flown && b === l) || (b === flown && a === l))) return true
  const sa = places.stationOf(a)
  const sb = places.stationOf(b)
  return sa !== null && sa === sb
}

/**
 * Where the player's ships are, for a panel at station `stationId` (where the flown ship is docked or moored): the
 * flown ship is here, the lease ship's entry keeps its own `stationId`, a fleet ship is where the fleet view says.
 */
export function placesAt(career: Career, stationId: string, ships: readonly { id: string; stationId: string | null }[]): ShipPlaces {
  const flown = flownIdOf(career)
  return {
    stationOf(fleetId: string): string | null {
      if (fleetId === flown) return stationId
      if (fleetId === OWN_SHIP_ID) return ownedShip(career, OWN_SHIP_ID)?.stationId ?? null
      return ships.find((s) => s.id === fleetId)?.stationId ?? null
    },
  }
}

/** The player's other ships together with the flown ship (hangar order), for the panel. */
export function transferPartners(career: Career, places: ShipPlaces): string[] {
  const flown = flownIdOf(career)
  const out: string[] = []
  for (const e of hangarOf(career)) if (e.fleetId !== flown && shipsTogether(career, flown, e.fleetId, places)) out.push(e.fleetId)
  return out
}

/** A ship's def, fit and hold: `career.ship`/`career.cargo` while flown, else its hangar entry and `hold`. */
interface HoldSide {
  defId: string
  fitted: ShipState['fitted']
  hold: CargoState
  capacityT: number
}

function sideOf(career: Career, fleetId: string, data: Pick<M2Data, 'modules'>, hulls: HullFitData): HoldSide | null {
  // The flown ship's live hold carries its capacity (the hold writers keep it at `holdCapacityT`, as `cargoFreeT` reads).
  if (fleetId === flownIdOf(career)) return { defId: career.ship.defId, fitted: career.ship.fitted, hold: career.cargo, capacityT: career.cargo.capacityT }
  const e = ownedShip(career, fleetId)
  if (e === null) return null
  const capacityT = holdCapacityT(e.defId, e.fitted, data, hulls)
  return { defId: e.defId, fitted: e.fitted, hold: e.hold ?? { capacityT, wares: emptyWares(), components: [] }, capacityT }
}

/** The career with ship `fleetId`'s hold set to `hold` (the flown ship's is `career.cargo`). */
function withHold(career: Career, fleetId: string, hold: CargoState): Career {
  if (fleetId === flownIdOf(career)) return { ...career, cargo: hold }
  return updateOwnedShip(career, fleetId, (e) => ({ ...e, hold }))
}

/** `hold` with `ware`'s processed tonnes set to `p`, clamped to [0, wares[ware]]; the key drops at 0. */
function withProcessed(hold: CargoState, ware: WareId, p: number): CargoState {
  const clamped = Math.min(Math.max(p, 0), hold.wares[ware])
  if (hold.processed === undefined && clamped <= EPSILON) return hold
  const processed: Partial<Record<WareId, number>> = {}
  for (const w of Object.keys(hold.processed ?? {}) as WareId[]) if (w !== ware) processed[w] = hold.processed?.[w]
  if (clamped > EPSILON) processed[ware] = clamped
  const out: CargoState = { ...hold, processed }
  if (Object.keys(processed).length === 0) delete out.processed
  return out
}

/** Moves the lot from `from` to `to` (both copies); null when `from` lacks it. */
function moveLot(from: CargoState, to: CargoState, lot: TransferLot): { from: CargoState; to: CargoState } | null {
  if (lot.kind === 'ware') {
    const have = from.wares[lot.ware]
    if (have + EPSILON < lot.tonnes) return null
    const t = Math.min(lot.tonnes, have)
    const p = Math.min(from.processed?.[lot.ware] ?? 0, have)
    const moved = have > 0 ? (p * t) / have : 0
    const left = have - t < EPSILON ? 0 : have - t
    const a = withProcessed({ ...from, wares: { ...from.wares, [lot.ware]: left } }, lot.ware, p - moved)
    const bWares = { ...to.wares, [lot.ware]: to.wares[lot.ware] + t }
    const b = withProcessed({ ...to, wares: bWares }, lot.ware, (to.processed?.[lot.ware] ?? 0) + moved)
    return { from: a, to: b }
  }
  if (lot.kind === 'good') {
    const have = from.parts?.[lot.good] ?? 0
    if (have + EPSILON < lot.qty) return null
    const q = Math.min(lot.qty, have)
    const left = have - q < EPSILON ? 0 : have - q
    return {
      from: { ...from, parts: { ...from.parts, [lot.good]: left } },
      to: { ...to, parts: { ...to.parts, [lot.good]: (to.parts?.[lot.good] ?? 0) + q } },
    }
  }
  const i = from.components.findIndex((c) => c.id === lot.id)
  if (i < 0) return null
  const c = from.components[i]
  return { from: { ...from, components: from.components.filter((_, k) => k !== i) }, to: { ...to, components: [...to.components, c] } }
}

/** True for a lot that must stay with the pilot (D-402): a contract, sealed or story component. */
function staysWithPilot(hold: CargoState, lot: TransferLot): boolean {
  if (lot.kind !== 'component') return false
  const c = hold.components.find((x) => x.id === lot.id)
  return c !== undefined && (c.contractId !== undefined || c.sealed === true || c.story === true)
}

/**
 * Moves cargo `lot` from ship `fromId`'s hold to ship `toId`'s (fleet ids; `OWN_SHIP_ID` = the lease ship). Refusals:
 * `invalidAmount` (the same ship, a bad amount), `notInLocker` (not the player's ship), `serviceUnavailable` (not
 * together, or pilot-bound cargo off the flown ship), `notInCargo` (the source lacks the lot), `cargoFull` (the
 * target's class-weighted hold can't take it; nothing moves).
 */
export function transferCargo(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  fromId: string,
  toId: string,
  lot: TransferLot,
  places: ShipPlaces,
  hulls: HullFitData = hullFitData(),
): CmdResult<Career> {
  if (fromId === toId) return fail('invalidAmount', 'The cargo is already aboard that ship.', { ship: fromId })
  if (lot.kind === 'ware' && !(Number.isFinite(lot.tonnes) && lot.tonnes > 0)) return invalidAmount('tonnes', lot.tonnes)
  if (lot.kind === 'good' && !(Number.isFinite(lot.qty) && lot.qty > 0)) return invalidAmount('quantity', lot.qty)
  const from = sideOf(career, fromId, data, hulls)
  if (from === null) return fail('notInLocker', `Ship ${fromId} is not yours.`, { ship: fromId })
  const to = sideOf(career, toId, data, hulls)
  if (to === null) return fail('notInLocker', `Ship ${toId} is not yours.`, { ship: toId })
  if (!shipsTogether(career, fromId, toId, places)) {
    return fail('serviceUnavailable', 'Both ships must be docked or moored at the same station.', { reason: 'notTogether' })
  }
  if (fromId === flownIdOf(career) && staysWithPilot(from.hold, lot)) {
    return fail('serviceUnavailable', 'Contract and story cargo stays aboard with you.', { reason: 'pilotCargo' })
  }
  const moved = moveLot(from.hold, to.hold, lot)
  if (moved === null) return fail('notInCargo', 'That cargo is not in the hold.', { ship: fromId })
  const target: CargoState = { ...moved.to, capacityT: to.capacityT }
  const used = holdUsedT(target, to.defId, hulls)
  if (used > to.capacityT + EPSILON) {
    return fail('cargoFull', `That would carry ${formatTonnes(used)} t but the ship holds only ${formatTonnes(to.capacityT)} t.`, {
      needed: used,
      capacity: to.capacityT,
    })
  }
  return ok(withHold(withHold(career, fromId, moved.from), toId, target))
}

/** Moves locker module `moduleId` between two ships that are together (P5a5a's `moveModule`; free). */
export function transferModule(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  fromId: string,
  toId: string,
  moduleId: ModuleId,
  places: ShipPlaces,
  hulls: HullFitData = hullFitData(),
): CmdResult<Career> {
  return moveModule(career, data, fromId, toId, moduleId, places, hulls)
}
